import { Injectable, OnDestroy, signal } from '@angular/core';

export type ToastVariant = 'success';

export interface ToastMessage {
  id: string;
  message: string;
  variant: ToastVariant;
}

export interface ToastOptions {
  durationMs?: number;
}

@Injectable({ providedIn: 'root' })
export class ToastService implements OnDestroy {
  private readonly toastMessages = signal<ToastMessage[]>([]);
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();
  private nextId = 0;

  readonly toasts = this.toastMessages.asReadonly();

  success(message: string, options: ToastOptions = {}): string {
    return this.show(message, 'success', options);
  }

  dismiss(id: string): void {
    this.clearTimer(id);
    this.toastMessages.update((toasts) =>
      toasts.filter((toast) => toast.id !== id)
    );
  }

  clear(): void {
    for (const id of this.timers.keys()) {
      this.clearTimer(id);
    }
    this.toastMessages.set([]);
  }

  ngOnDestroy(): void {
    this.clear();
  }

  private show(
    message: string,
    variant: ToastVariant,
    options: ToastOptions
  ): string {
    const id = `toast-${++this.nextId}`;
    const toast: ToastMessage = { id, message, variant };
    const durationMs = options.durationMs ?? 4000;

    this.toastMessages.update((toasts) => [...toasts, toast]);

    if (durationMs > 0) {
      this.timers.set(
        id,
        setTimeout(() => this.dismiss(id), durationMs)
      );
    }

    return id;
  }

  private clearTimer(id: string): void {
    const timer = this.timers.get(id);
    if (!timer) return;

    clearTimeout(timer);
    this.timers.delete(id);
  }
}
